import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import TitleWithBack from '../../components/common/TitleWithBack';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import PaginatedTable from '../../components/PaginatedTable';
import { useDialog } from '../../context/DialogContext';

// Shared interface for OSM and RLP - both work the same way: marksheet
// (.xlsx) and scoring guide (.pdf) downloadable per exam centre/series/
// module, individually or in bulk for a selection. `examType` picks which
// exam type's assignments this instance shows.
export default function DataTeamSeriesWorkDownload({ examType }) {
  const { alert } = useDialog();
  const { seriesId } = useParams();
  const navigate = useNavigate();
  const [seriesList, setSeriesList] = useState([]);
  const [seriesName, setSeriesName] = useState('');
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState({
    module: '',
    moduleCode: '',
    examCenterNumber: '',
    examCenterName: '',
    year: '',
    semester: ''
  });
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  // Fetch all series for dropdown
  useEffect(() => {
    axios.get('/api/exam-series')
      .then(res => setSeriesList(res.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    // First get series name
    axios
      .get(`/api/exam-series/${seriesId}`)
      .then((res) => {
        const d = res.data;
        setSeriesName(d.name || `Series ${seriesId}`);
      })
      .catch(() => {
        setError('Error fetching series data.');
        setLoading(false);
      });

    // Then get assignments with uploads, scoped server-side to this exam type
    axios
      .get(`/api/exam-series/${seriesId}/assignments-with-uploads`, { params: { examType } })
      .then((res) => {
        const assignments = res.data || [];
        setTableData(assignments);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching assignments data.');
        setLoading(false);
      });
  }, [seriesId, examType]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const filteredData = tableData.filter((r) => {
    if (r.status !== 'COMPLETED') return false;
    if (filters.module && !r.moduleName.toLowerCase().includes(filters.module.toLowerCase())) return false;
    if (filters.moduleCode && !r.moduleCode.toLowerCase().includes(filters.moduleCode.toLowerCase())) return false;
    if (filters.examCenterNumber && !r.examCentreNumber.toLowerCase().includes(filters.examCenterNumber.toLowerCase())) return false;
    if (filters.examCenterName && !r.examCentreName.toLowerCase().includes(filters.examCenterName.toLowerCase())) return false;
    if (filters.year && !r.year.toLowerCase().includes(filters.year.toLowerCase())) return false;
    if (filters.semester && !r.semester.toLowerCase().includes(filters.semester.toLowerCase())) return false;
    return true;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = (a[sortColumn] || '').toString().toLowerCase();
    const valB = (b[sortColumn] || '').toString().toLowerCase();
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortOrder('asc');
    }
  };

  const handleRowSelect = (row) => {
    setSelectedRows((prev) => {
      const exists = prev.find(r => r.assignmentId === row.assignmentId);
      if (exists) {
        return prev.filter(r => r.assignmentId !== row.assignmentId);
      } else {
        return [...prev, row];
      }
    });
  };

  const isRowSelected = (row) => selectedRows.some(r => r.assignmentId === row.assignmentId);

  // Only keep comprehensive documents
  const isComprehensiveDownload = (doc) => {
    return doc.documentType?.toLowerCase().includes('comprehensive');
  };

  const sanitize = str => (str || '').replace(/[^a-zA-Z0-9]/g, '');

  // One request for every selected row: the backend groups them by exam
  // centre and returns a single "zip of zips" (one inner zip per centre,
  // bundled into one outer zip) - not a separate flat zip per row.
  const handleDownloadSelectedZip = async () => {
    if (!selectedRows.length) return;
    try {
      const assignmentIdsQuery = selectedRows.map(r => `assignmentIds=${r.assignmentId}`).join('&');
      const res = await axios.get(
        `/api/exam-series/${seriesId}/download-zip-filtered?${assignmentIdsQuery}`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(seriesName) || 'series'}_practical_work.zip`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('Failed to download zip for the selected assignments.', { title: 'Error', variant: 'danger' });
    }
  };

  // Bulk-downloads just the marksheets/scoring guides (not the raw uploaded
  // documents) for every selected row, bundled into one zip.
  const handleDownloadSelectedScoring = async () => {
    if (!selectedRows.length) return;
    try {
      const assignmentIdsQuery = selectedRows.map(r => `assignmentIds=${r.assignmentId}`).join('&');
      const res = await axios.get(
        `/api/scoring-criteria/bulk-download?${assignmentIdsQuery}`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(seriesName) || 'series'}_${examType.toLowerCase()}_marksheets_and_scoring_guides.zip`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No marksheets or scoring guides available for the selected assignments.', { title: 'Unavailable', variant: 'warning' });
    }
  };

  // Scoring guide downloads only apply to modules with a scoring guide set
  // up (RLP/OSM) - the backend returns a plain error for anything else,
  // which we surface as-is rather than trying to pre-filter by exam type.
  const handleDownloadMarksheet = async (row) => {
    try {
      const res = await axios.get(
        `/api/scoring-criteria/assignment/${row.assignmentId}/marksheet`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_final-marksheet.xlsx`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No marksheet available for this module (a scoring guide may not have been set up or scored yet).', { title: 'Unavailable', variant: 'warning' });
    }
  };

  const handleDownloadScoringGuide = async (row) => {
    try {
      const res = await axios.get(
        `/api/scoring-criteria/assignment/${row.assignmentId}/general-guide`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_scoring-guide.xlsx`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No scoring guide available for this module (it may not have been set up, or no candidates were found for this centre).', { title: 'Unavailable', variant: 'warning' });
    }
  };

  // PDF versions of the same two documents - Data Team/Security only
  // (examiners get read-only in-browser preview instead, no download at
  // all, of either format).
  const handleDownloadMarksheetPdf = async (row) => {
    try {
      const res = await axios.get(
        `/api/scoring-criteria/assignment/${row.assignmentId}/marksheet/pdf`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_final-marksheet.pdf`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No marksheet available for this module (a scoring guide may not have been set up or scored yet).', { title: 'Unavailable', variant: 'warning' });
    }
  };

  const handleDownloadScoringGuidePdf = async (row) => {
    try {
      const res = await axios.get(
        `/api/scoring-criteria/assignment/${row.assignmentId}/general-guide/pdf`,
        { responseType: 'blob' }
      );
      const filename = `${sanitize(row.examCentreNumber)}_${sanitize(row.examCentreName)}_${sanitize(row.moduleCode)}_scoring-guide.pdf`;
      saveAs(res.data, filename);
    } catch (e) {
      alert('No scoring guide available for this module (it may not have been set up, or no candidates were found for this centre).', { title: 'Unavailable', variant: 'warning' });
    }
  };

  // Define columns for PaginatedTable
  const columns = [
    { key: 'select', header: '' },
    { key: 'serial', header: <>#</> },
    { key: 'examCentreNumber', header: <span onClick={() => handleSort('examCentreNumber')} style={{cursor: 'pointer'}}>Center no {sortColumn === 'examCentreNumber' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'examCentreName', header: <span onClick={() => handleSort('examCentreName')} style={{cursor: 'pointer'}}>Center Name {sortColumn === 'examCentreName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'moduleCode', header: <span onClick={() => handleSort('moduleCode')} style={{cursor: 'pointer'}}>Module Code {sortColumn === 'moduleCode' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'moduleName', header: <span onClick={() => handleSort('moduleName')} style={{cursor: 'pointer'}}>Module Name {sortColumn === 'moduleName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'seriesName', header: <span onClick={() => handleSort('seriesName')} style={{cursor: 'pointer'}}>Series {sortColumn === 'seriesName' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'year', header: <span onClick={() => handleSort('year')} style={{cursor: 'pointer'}}>Year {sortColumn === 'year' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'semester', header: <span onClick={() => handleSort('semester')} style={{cursor: 'pointer'}}>Semester {sortColumn === 'semester' && (sortOrder === 'asc' ? '\u25b2' : '\u25bc')}</span> },
    { key: 'uploads', header: 'Uploads' },
    { key: 'status', header: 'Status' },
    { key: 'scoringGuide', header: 'Downloads' }
  ];

  // Render row function for PaginatedTable
  const renderRow = (r, idx, pageIndex, pageSize) => (
    <>
      <td onClick={e => e.stopPropagation()}>
        <Form.Check
          type="checkbox"
          name="rowSelect"
          checked={isRowSelected(r)}
          onChange={() => handleRowSelect(r)}
          aria-label={`Select row ${(pageIndex * pageSize) + idx + 1}`}
        />
      </td>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td><small>{r.examCentreNumber}</small></td>
      <td><small>{r.examCentreName}</small></td>
      <td><small>{r.moduleCode}</small></td>
      <td><small>{r.moduleName}</small></td>
      <td><small>{r.seriesName}</small></td>
      <td><small>{r.year}</small></td>
      <td><small>{r.semester}</small></td>
      <td>
        {r.documents && r.documents.length > 0 ? (
          <div>
            {r.documents.filter(isComprehensiveDownload).map((doc, docIndex) => (
              <div key={docIndex} className="mb-1">
                <Badge bg="info" className="me-1">
                  {doc.documentType}
                </Badge>
               
                {doc.studentName && (
                  <small className="text-muted ms-1">({doc.studentName})</small>
                )}
              </div>
            ))}
          </div>
        ) : (
          <small><span className="text-muted">No upload</span></small>
        )}
      </td>
      <td>
        <small>
        <Badge bg={r.status === 'COMPLETED' ? 'success' : r.status === 'ASSIGNED' ? 'warning' : 'secondary'}>
          {r.status}
        </Badge></small>
      </td>
      <td onClick={e => e.stopPropagation()}>
        <div className="d-flex flex-column gap-1">
          <Button size="sm" variant="outline-primary" onClick={() => handleDownloadMarksheet(r)}>
            Marksheet (.xlsx)
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => handleDownloadScoringGuide(r)}>
            Scoring Guide (.xlsx)
          </Button>
          <Button size="sm" variant="outline-primary" onClick={() => handleDownloadMarksheetPdf(r)}>
            Marksheet (.pdf)
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={() => handleDownloadScoringGuidePdf(r)}>
            Scoring Guide (.pdf)
          </Button>
        </div>
      </td>
    </>
  );

  if (loading) return <div className="mt-4 container">Loading...</div>;
  if (error) return <div className="mt-4 container text-danger">{error}</div>;

  return (
    <Container className="mt-2" fluid>
      <TitleWithBack title={`${examType} practical work download (${seriesName})`} />
      {/* Series selection dropdown */}
      <Row className="mb-3">
        <Col md={4} className="mb-2">
          <Form.Label>Select Series</Form.Label>
          <Form.Select
            value={seriesId}
            onChange={e => navigate(`/data-team/${examType.toLowerCase()}/series/${e.target.value}`)}
          >
            {seriesList.map(series => (
              <option key={series.id} value={series.id}>
                {series.name} ({series.startDate} to {series.endDate})
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={2}>
          <Form.Label>Center no</Form.Label>
          <Form.Control
            type="text"
            name="examCenterNumber"
            value={filters.examCenterNumber}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Center Name</Form.Label>
          <Form.Control
            type="text"
            name="examCenterName"
            value={filters.examCenterName}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Module Code</Form.Label>
          <Form.Control
            type="text"
            name="moduleCode"
            value={filters.moduleCode}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Module name</Form.Label>
          <Form.Control
            type="text"
            name="module"
            value={filters.module}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Year</Form.Label>
          <Form.Control
            type="text"
            name="year"
            value={filters.year}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Label>Semester</Form.Label>
          <Form.Control
            type="text"
            name="semester"
            value={filters.semester}
            onChange={handleFilterChange}
          />
        </Col>
      </Row>
      {sortedData.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable
          data={sortedData}
          columns={columns}
          renderRow={renderRow}
          rowsPerPage={50}
        />
      )}
      <div className="text-end mt-3 d-flex justify-content-end gap-2">
        <Button variant="outline-primary" onClick={handleDownloadSelectedScoring} disabled={!selectedRows.length}>
          Download marksheets + scoring guides (selected)
        </Button>
        <Button variant="primary" onClick={handleDownloadSelectedZip} disabled={!selectedRows.length}>
          Download raw uploads (zip)
        </Button>
      </div>
    </Container>
  );
}